import { useEffect, useState } from "react";
import { Target, Zap, ChevronRight, X, Check, Settings, CheckCircle, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useBrands } from '@/lib/BrandsProvider';
import useBrandAutopostSettings from '@/components/brand/hooks/useBrandAutopostSettings';
import usePlatformSettings from '@/components/brand/hooks/usePlatformSettings';
import { BrandProgress } from '@/components/brand/BrandProgress';
import fieldMetadata from '@/lib/fieldMetadata.json';

interface NextStepsForSuccessProps {
  onDismiss?: () => void;
}

export function NextStepsForSuccess({ onDismiss }: NextStepsForSuccessProps = {}) {
  const { selectedBrand } = useBrands();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const { brandAutopostSettings, loading: autopostLoading } = useBrandAutopostSettings(selectedBrand?.id);
  const { platforms, platformSettings, loading: platformsLoading } = usePlatformSettings(selectedBrand?.id);
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());

  // Define the steps with their details
  const steps = [
    {
      id: 'configure-automation',
      title: 'Configure Automation',
      description: 'Set up automated content creation and posting preferences',
      icon: Settings,
      link: '/automation',
      completed: false,
      extraContent: (
        <div className="p-4 bg-gradient-to-br from-indigo-50 via-blue-50/50 to-purple-50 rounded-xl border border-indigo-200/50">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Settings className="h-5 w-5 text-indigo-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-indigo-900 mb-1">
                Fine-tune Your Automation
              </p>
              <p className="text-sm text-indigo-700">
                This is where you can customize your automation settings to match your workflow.
                Control posting schedules, content frequency, approval processes, and more.
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'brand-genius-2',
      title: 'Brand Genius Part 2',
      description: 'Teach Rainfactory more so it better understands your brand and your business. Get your completion status to 80%< for a strong brand.',
      icon: Zap,
      link: selectedBrand ? `/brands/${selectedBrand.id}` : '/brand/genius',
      completed: false
    }
  ];

  useEffect(() => {
    async function checkCompletedSteps() {
      if (!selectedBrand) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const completed = new Set<string>();

        // Check for automation configuration
        // Automation is only considered complete if BOTH conditions are met:
        // 1. Brand master control is enabled (autogenerate_enabled === true)
        // 2. At least one platform has auto-create enabled (autogenerate_enabled === true)
        if (brandAutopostSettings?.autogenerate_enabled) {
          // Check if at least one platform has auto-create enabled
          const hasEnabledPlatforms = platforms.some(
            platform => platformSettings[platform.id]?.autogenerate_enabled === true
          );

          if (hasEnabledPlatforms) {
            completed.add('configure-automation');
          }
        }

        // Check if brand has detailed information (Brand Genius Part 2)
        if (selectedBrand.elevator_pitch && selectedBrand.target_market && selectedBrand.brand_voice) {
          completed.add('brand-genius-2');
        }

        // Also mark brand-genius-2 as complete if progress is >= 80%
        const calculateProgress = () => {
          if (!selectedBrand) return 0;
          const visibleFields = Object.keys(fieldMetadata).filter(key => {
            const metadata = fieldMetadata[key as keyof typeof fieldMetadata];
            return !metadata?.hidden && key !== 'status';
          });
          const totalFields = visibleFields.length;
          const filledFields = visibleFields.filter(key => {
            const value = selectedBrand[key as keyof typeof selectedBrand];
            return value !== null && value !== undefined && value !== '';
          }).length;
          return (filledFields / totalFields) * 100;
        };

        const progress = calculateProgress();
        if (progress >= 80) {
          completed.add('brand-genius-2');
        }

        setCompletedSteps(completed);
      } catch (error) {
        console.error('Error checking completed steps:', error);
      } finally {
        setIsLoading(false);
      }
    }

    checkCompletedSteps();
  }, [selectedBrand, brandAutopostSettings, autopostLoading, platforms, platformSettings, platformsLoading]);

  // Find the next uncompleted step or use selectedStepId if provided
  const getDisplayStep = () => {
    if (selectedStepId) {
      const step = steps.find(s => s.id === selectedStepId);
      if (step) return step;
    }
    
    // Find the first uncompleted step
    const nextStep = steps.find(step => !completedSteps.has(step.id));
    return nextStep || steps[0]; // Fallback to first step
  };

  const displayStep = getDisplayStep();
  
  // Check if all steps are completed
  const allStepsCompleted = completedSteps.size === steps.length;

  if (isLoading || autopostLoading || platformsLoading) {
    return (
      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        <div className="p-4 border-b bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center shadow-lg">
                <Target className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-amber-900 dark:text-amber-100 flex items-center gap-2">
                  Next Steps for Success
                </h2>
                <p className="text-sm text-amber-700 dark:text-amber-200">
                  Complete these steps to maximize your brand's potential
                </p>
              </div>
            </div>
            <span className="text-sm text-amber-700 dark:text-amber-300 font-medium">
              Loading...
            </span>
          </div>
        </div>
        
        <div className="flex items-center justify-center h-48">
          <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
        </div>
        <div className="h-2 bg-gradient-to-r from-amber-200 via-orange-200 to-red-200 dark:from-amber-800 dark:via-orange-800 dark:to-red-800" />
      </div>
    );
  }

  // Show success banner when all steps are completed
  if (allStepsCompleted) {
    return (
      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        <div className="p-4 border-b bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg">
                <CheckCircle className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-green-900 dark:text-green-100 flex items-center gap-2">
                  🎉 All Set Up!
                </h2>
                <p className="text-sm text-green-700 dark:text-green-200">
                  You've completed all essential setup steps
                </p>
              </div>
            </div>
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="text-green-600 hover:text-green-800 dark:text-green-300 dark:hover:text-green-200 transition-colors"
                title="Hide Next Steps"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
            <Sparkles className="h-4 w-4" />
            <span className="text-sm font-medium">
              Congratulations! Your brand is ready to shine.
            </span>
          </div>
        </div>
        <div className="h-2 bg-gradient-to-r from-green-200 via-emerald-200 to-teal-200 dark:from-green-800 dark:via-emerald-800 dark:to-teal-800" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
      {/* Header with gradient background matching dashboard pattern */}
      <div className="p-4 border-b bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
              <Target className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-semibold text-amber-900 dark:text-amber-100">
                Next Steps for Success
              </h2>
              <p className="text-xs sm:text-sm text-amber-700 dark:text-amber-200 truncate">
                Complete these steps to maximize your brand's potential
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs sm:text-sm text-amber-700 dark:text-amber-300 font-medium">
              {completedSteps.size}/{steps.length}
            </span>
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="text-amber-600 hover:text-amber-800 dark:text-amber-300 dark:hover:text-amber-200 transition-colors"
                title="Hide Next Steps"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main content area - Vertical layout */}
      <div className="flex flex-col">
        {/* Progress Overview Section */}
        <div className="border-b border-gray-200">
          <div className="p-3 sm:p-4 bg-gray-50/50">
            <span className="text-sm font-medium text-gray-700">Progress Overview</span>
          </div>

          <div className="divide-y divide-gray-100">
            {steps.map((step, index) => {
              const isCompleted = completedSteps.has(step.id);
              const isSelected = displayStep.id === step.id;
              const Icon = step.icon;

              return (
                <div key={step.id} className="flex flex-col">
                  {/* Step header - clickable on all screen sizes */}
                  <button
                    onClick={() => setSelectedStepId(step.id)}
                    className={`w-full text-left p-3 sm:p-4 hover:bg-gray-50 transition-colors ${
                      isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex items-center justify-center mt-1 flex-shrink-0">
                        {isCompleted ? (
                          <div className="w-6 h-6 flex items-center justify-center bg-green-100 rounded-full">
                            <Check className="h-4 w-4 text-green-600" />
                          </div>
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-semibold">
                            {index + 1}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Icon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                          <h3 className={`text-sm font-medium ${
                            isCompleted ? 'text-green-900' : 'text-gray-900'
                          } ${isSelected ? 'text-blue-900' : ''}`}>
                            {step.title}
                          </h3>
                          {isCompleted && (
                            <span className="text-xs text-green-600 ml-auto">Completed</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                          {step.description}
                        </p>
                      </div>
                      <ChevronRight className={`h-5 w-5 text-gray-400 flex-shrink-0 transition-transform ${
                        isSelected ? 'rotate-90' : ''
                      }`} />
                    </div>
                  </button>

                  {/* Expanded content - shown when selected */}
                  {isSelected && (
                    <div className="border-t border-gray-100 bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
                      <div className="p-4 sm:p-6">
                        <div className="space-y-4">
                          {/* Special content for Brand Genius Part 2 */}
                          {displayStep.id === 'brand-genius-2' && (
                            <div>
                              <BrandProgress
                                brand={selectedBrand}
                                showLink={false}
                                className="bg-white border shadow-sm"
                              />
                            </div>
                          )}

                          {/* Extra content like resource thumbnails or platform icons */}
                          {displayStep.extraContent && (
                            <div>
                              {displayStep.extraContent}
                            </div>
                          )}

                          {/* Action button */}
                          <div className="flex gap-3 pt-2">
                            {displayStep.link === '#' ? (
                              <div className="text-sm text-muted-foreground italic">
                                This step requires interaction
                              </div>
                            ) : (
                              <Link
                                to={displayStep.link}
                                className={`inline-flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-sm font-medium transition-colors w-full sm:w-auto justify-center ${
                                  completedSteps.has(displayStep.id)
                                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                    : 'bg-primary text-primary-foreground hover:bg-primary/90'
                                }`}
                              >
                                {completedSteps.has(displayStep.id) ? 'View Details' : 'Get Started'}
                                <ChevronRight className="h-4 w-4" />
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="h-2 bg-gradient-to-r from-amber-200 via-orange-200 to-red-200 dark:from-amber-800 dark:via-orange-800 dark:to-red-800" />
    </div>
  );
}

export default NextStepsForSuccess; 